import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  InputAdornment,
  IconButton,
  Chip,
  Autocomplete,
  Checkbox,
  TableSortLabel
} from '@mui/material';
import {
  Search as SearchIcon,
  QrCode as QrCodeIcon,
  Close as CloseIcon,
  Inventory as InventoryIcon,
  Add as AddIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { searchProductVariants, searchProductVariantsByBarcode } from '../../services/inventoryCountService';
import { Product, ProductVariant } from '../../types/product';
import { ProductAttribute } from '../../types/product';
import { Warehouse } from '../../types/warehouse';
import { getProductWithWarehouseInventory } from '../../services/productService';

// نوع نتيجة البحث عن متغير المنتج للجرد
interface InventoryProductVariantSearchResult {
  productId: string;
  productName: string;
  variantId: string;
  variantAttributes?: Record<string, string>;
  barcode?: string;
  unitId: string;
  unitName: string;
}

interface InventoryProductSearchProps {
  onSelectVariant: (variant: InventoryProductVariantSearchResult) => void;
  onSelectVariants?: (items: InventoryProductVariantSearchResult[]) => void;
  open: boolean;
  onClose: () => void;
  selectedCategories?: string[];
  productSelectionMode?: 'all' | 'category' | 'specific';
  multiSelect?: boolean;
  warehouseId?: string;
}

const InventoryProductSearch: React.FC<InventoryProductSearchProps> = ({
  onSelectVariant,
  onSelectVariants,
  open,
  onClose,
  selectedCategories = [],
  productSelectionMode = 'all',
  multiSelect = false,
  warehouseId
}) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
  const [attributes, setAttributes] = useState<ProductAttribute[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [loading, setLoading] = useState(false);
  const [barcode, setBarcode] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAttributes, setSelectedAttributes] = useState<Record<string, string[]>>({});
  const [inventoryDialogOpen, setInventoryDialogOpen] = useState(false);
  const [selectedVariantForInventory, setSelectedVariantForInventory] = useState<{product: Product, variant: ProductVariant} | null>(null);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);
  const [selectedItems, setSelectedItems] = useState<InventoryProductVariantSearchResult[]>([]);
  const barcodeInputRef = useRef<HTMLInputElement>(null);

  // Focus barcode input when dialog opens
  useEffect(() => {
    if (open && barcodeInputRef.current) {
      setTimeout(() => {
        barcodeInputRef.current?.focus();
      }, 100);
    }
  }, [open]);

  // Fetch initial data (attributes and warehouses only)
  useEffect(() => {
    if (open) {
      fetchInitialData();
    }
  }, [open]);

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      const { apiWithContext } = await import('../../services/api');
      
      const [attributesData, warehousesData] = await Promise.all([
        apiWithContext.get('/product-attributes', 'inventory-count').then(res => res.data),
        apiWithContext.get('/warehouses?status=active', 'inventory-count').then(res => res.data)
      ]);
      
      console.log('Attributes data:', attributesData);
      
      setProducts([]);
      setFilteredProducts([]);
      setAttributes(attributesData || []);
      setWarehouses(warehousesData.warehouses || warehousesData || []);
    } catch (error) {
      console.error('Error fetching initial data:', error);
      setAttributes([]);
      setWarehouses([]);
    } finally {
      setLoading(false);
    }
  };

  // Dynamic product search function
  const searchProducts = async (searchTerm: string) => {
    if (!searchTerm.trim()) {
      setProducts([]);
      setFilteredProducts([]);
      setIsLoadingProducts(false);
      return;
    }

    if (searchTerm.trim().length < 3) {
      setProducts([]);
      setFilteredProducts([]);
      setIsLoadingProducts(false);
      return;
    }

    setIsLoadingProducts(true);
    try {
      const { apiWithContext } = await import('../../services/api');
      const response = await apiWithContext.get(
        `/products?search=${encodeURIComponent(searchTerm)}&limit=50`,
        'inventory-count'
      );
      
      console.log(`🔍 InventoryProductSearch - استجابة البحث:`, response);
      
      if (response.data && response.data.products && Array.isArray(response.data.products)) {
        const products = response.data.products;
        console.log(`🔍 InventoryProductSearch - نتائج البحث: ${products.length} منتج`);
        setProducts(products);
        setFilteredProducts(products);
      } else {
        setProducts([]);
        setFilteredProducts([]);
      }
    } catch (error) {
      console.error('خطأ في البحث عن المنتجات:', error);
      setProducts([]);
      setFilteredProducts([]);
    } finally {
      setIsLoadingProducts(false);
    }
  };

  // Handle search with delay
  const handleProductSearch = (searchTerm: string) => {
    if (!searchTerm.trim()) {
      setProducts([]);
      setFilteredProducts([]);
      setIsLoadingProducts(false);
      if (searchTimeout) {
        clearTimeout(searchTimeout);
        setSearchTimeout(null);
      }
      return;
    }

    if (searchTerm.trim().length < 3) {
      setProducts([]);
      setFilteredProducts([]);
      setIsLoadingProducts(false);
      if (searchTimeout) {
        clearTimeout(searchTimeout);
        setSearchTimeout(null);
      }
      return;
    }

    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    
    const timeout = setTimeout(() => {
      searchProducts(searchTerm);
    }, 500);
    
    setSearchTimeout(timeout);
  };

  // Handle barcode search
  const handleBarcodeSearch = async () => {
    if (!barcode.trim()) return;

    setIsLoadingProducts(true);
    try {
      const response = await searchProductVariantsByBarcode(barcode.trim());
      
      if (response.success && response.variant) {
        console.log(`✅ تم العثور على المنتج بالباركود: ${response.variant.productName}`);
        
        if (multiSelect || onSelectVariants) {
          const variant: InventoryProductVariantSearchResult = {
            productId: response.variant.productId,
            productName: response.variant.productName,
            variantId: response.variant.variantId,
            variantAttributes: response.variant.variantAttributes || {},
            barcode: response.variant.barcode,
            unitId: response.variant.unitId,
            unitName: response.variant.unitName || response.variant.unitId
          };
          
          setSelectedItems(prev => {
            const exists = prev.some(item => item.variantId === variant.variantId);
            if (exists) {
              return prev.filter(item => item.variantId !== variant.variantId);
            }
            return [...prev, variant];
          });
        } else if (onSelectVariant) {
          const variant: InventoryProductVariantSearchResult = {
            productId: response.variant.productId,
            productName: response.variant.productName,
            variantId: response.variant.variantId,
            variantAttributes: response.variant.variantAttributes || {},
            barcode: response.variant.barcode,
            unitId: response.variant.unitId,
            unitName: response.variant.unitName || response.variant.unitId
          };
          onSelectVariant(variant);
          handleDialogClose();
        }
      } else {
        alert('لم يتم العثور على منتج بهذا الباركود');
      }
    } catch (error) {
      console.error('خطأ في البحث بالباركود:', error);
      alert('لم يتم العثور على منتج بهذا الباركود');
    } finally {
      setIsLoadingProducts(false);
    }
  };

  // Handle pressing Enter in barcode field
  const handleBarcodeKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleBarcodeSearch();
    }
  };

  // Handle attribute selection
  const handleAttributeChange = (attributeName: string, values: string[]) => {
    setSelectedAttributes(prev => {
      const updated = { ...prev };
      if (values.length === 0) {
        delete updated[attributeName];
      } else {
        updated[attributeName] = values;
      }
      return updated;
    });
  };

  // Handle search term changes with dynamic search
  useEffect(() => {
    handleProductSearch(searchTerm);
  }, [searchTerm]);

  // Filter products based on selected attributes and page category settings
  useEffect(() => {
    if (products.length === 0) {
      setFilteredProducts([]);
      return;
    }
    
    let results = products;
    
    // Apply category filtering from page settings
    if (productSelectionMode === 'category' && selectedCategories.length > 0) {
      results = results.filter(product => {
        const hasMatchingVariant = product.variants.some(variant => {
          if (variant.categories && variant.categories.length > 0) {
            const variantMatch = variant.categories.some(categoryId => selectedCategories.includes(categoryId));
            if (variantMatch) return true;
          }
          
          const barcodeMatch = variant.barcodes.some(barcode => {
            return barcode.categories && barcode.categories.some(categoryId =>
              selectedCategories.includes(categoryId)
            );
          });
          
          return barcodeMatch;
        });
        
        return hasMatchingVariant;
      });
    }
    
    // Apply attribute filtering
    if (Object.keys(selectedAttributes).length > 0) {
      results = results.filter(product => {
        return product.variants.some(variant => {
          return Object.entries(selectedAttributes).every(([attrName, selectedValues]) => {
            const variantValue = variant.attributes[attrName];
            return selectedValues.includes(variantValue);
          });
        });
      });
    }
    
    setFilteredProducts(results);
  }, [selectedAttributes, products, selectedCategories, productSelectionMode]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [searchTimeout]);

  // Collect all possible values for each attribute
  const getAttributeValues = (attributeName: string): string[] => {
    const values = new Set<string>();
    
    products.forEach(product => {
      product.variants.forEach(variant => {
        if (variant.attributes[attributeName]) {
          values.add(variant.attributes[attributeName]);
        }
      });
    });
    
    return Array.from(values);
  };

  // Handle showing inventory for a variant
  const handleShowInventory = async (product: Product, variant: ProductVariant) => {
    console.log('🚀 فتح نافذة المخزون للمنتج:', product.name);
    
    if (!product._id) {
      console.error('❌ معرف المنتج غير موجود');
      return;
    }
    
    try {
      const productWithInventory = await getProductWithWarehouseInventory(product._id, 'inventory-count');
      console.log('📦 المنتج مع بيانات المخزون:', productWithInventory);
      
      if (productWithInventory.variants) {
        const updatedVariant = productWithInventory.variants.find(v => v._id === variant._id);
        if (updatedVariant && updatedVariant.warehouseInventory) {
          console.log('✅ تم العثور على بيانات المخزون:', updatedVariant.warehouseInventory);
          setSelectedVariantForInventory({ product, variant: updatedVariant });
        } else {
          console.log('⚠️ لم يتم العثور على بيانات المخزون للمتغير');
          setSelectedVariantForInventory({ product, variant });
        }
      } else {
        console.log('⚠️ لم يتم العثور على متغيرات في المنتج');
        setSelectedVariantForInventory({ product, variant });
      }
    } catch (error) {
      console.error('❌ خطأ في جلب بيانات المخزون:', error);
      setSelectedVariantForInventory({ product, variant });
    }
    
    setInventoryDialogOpen(true);
  };

  // Close inventory dialog
  const handleCloseInventoryDialog = () => {
    setInventoryDialogOpen(false);
    setSelectedVariantForInventory(null);
  };

  // Reset form when dialog closes
  const handleDialogClose = () => {
    setBarcode('');
    setSearchTerm('');
    setSelectedAttributes({});
    setProducts([]);
    setFilteredProducts([]);
    setSelectedItems([]);
    
    if (searchTimeout) {
      clearTimeout(searchTimeout);
      setSearchTimeout(null);
    }
    
    onClose();
  };

  const handleToggleSelection = (product: Product, variant: ProductVariant) => {
    setSelectedItems(prev => {
      const exists = prev.some(item => item.variantId === variant._id);
      if (exists) {
        return prev.filter(item => item.variantId !== variant._id);
      }
      return [...prev, {
        productId: product._id || '',
        productName: product.name || '',
        variantId: variant._id || '',
        variantAttributes: variant.attributes || {},
        barcode: variant.barcodes[0]?.code || '',
        unitId: variant.barcodes[0]?.unit || variant.unit || '',
        unitName: variant.barcodes[0]?.unit || variant.unit || ''
      }];
    });
  };

  const isItemSelected = (variantId: string) => {
    return selectedItems.some(item => item.variantId === variantId);
  };

  const handleSelectAll = () => {
    const allVisibleItems: InventoryProductVariantSearchResult[] = [];
    
    filteredProducts.forEach(product => {
      product.variants.forEach(variant => {
        if (Object.keys(selectedAttributes).length > 0) {
          const matchesAllAttributes = Object.entries(selectedAttributes).every(([attrName, selectedValues]) => {
            const variantValue = variant.attributes[attrName];
            return selectedValues.includes(variantValue);
          });
          if (!matchesAllAttributes) return;
        }
        
        if (productSelectionMode === 'category' && selectedCategories.length > 0) {
          let variantAllowed = false;
          
          if (variant.categories && variant.categories.length > 0) {
            variantAllowed = variant.categories.some(categoryId => selectedCategories.includes(categoryId));
          }
          
          if (!variantAllowed) {
            variantAllowed = variant.barcodes.some(barcode => {
              return barcode.categories && barcode.categories.some(categoryId =>
                selectedCategories.includes(categoryId)
              );
            });
          }
          
          if (!variantAllowed) return;
        }
        
        allVisibleItems.push({
          productId: product._id || '',
          productName: product.name || '',
          variantId: variant._id || '',
          variantAttributes: variant.attributes || {},
          barcode: variant.barcodes[0]?.code || '',
          unitId: variant.barcodes[0]?.unit || variant.unit || '',
          unitName: variant.barcodes[0]?.unit || variant.unit || ''
        });
      });
    });
    
    setSelectedItems(allVisibleItems);
  };

  const handleAddSelected = () => {
    if (onSelectVariants) {
      onSelectVariants(selectedItems);
    }
    handleDialogClose();
  };

  // Count total visible items matching filters
  const getTotalVisibleItems = () => {
    let count = 0;
    filteredProducts.forEach(product => {
      product.variants.forEach(variant => {
        if (Object.keys(selectedAttributes).length > 0) {
          const matchesAllAttributes = Object.entries(selectedAttributes).every(([attrName, selectedValues]) => {
            const variantValue = variant.attributes[attrName];
            return selectedValues.includes(variantValue);
          });
          if (!matchesAllAttributes) return;
        }
        
        if (productSelectionMode === 'category' && selectedCategories.length > 0) {
          let variantAllowed = false;
          
          if (variant.categories && variant.categories.length > 0) {
            variantAllowed = variant.categories.some(categoryId => selectedCategories.includes(categoryId));
          }
          
          if (!variantAllowed) {
            variantAllowed = variant.barcodes.some(barcode => {
              return barcode.categories && barcode.categories.some(categoryId =>
                selectedCategories.includes(categoryId)
              );
            });
          }
          
          if (!variantAllowed) return;
        }
        
        count++;
      });
    });
    return count;
  };

  return (
    <>
    <Dialog
      open={open}
      onClose={handleDialogClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ style: { minHeight: '70vh' } }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">البحث المتقدم عن المنتجات - الجرد</Typography>
          <IconButton onClick={handleDialogClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent dividers>
        <Box sx={{ mb: 3 }}>
          {/* Barcode Scanner */}
          <TextField
            fullWidth
            label="قراءة الباركود"
            value={barcode}
            onChange={(e) => setBarcode(e.target.value)}
            onKeyDown={handleBarcodeKeyDown}
            margin="normal"
            variant="outlined"
            inputRef={barcodeInputRef}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={handleBarcodeSearch} edge="end">
                    <QrCodeIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          
          {/* General Search */}
          <TextField
            fullWidth
            label="بحث عن منتج (ابدأ بالكتابة)"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            margin="normal"
            variant="outlined"
            placeholder="ابدأ بكتابة اسم المنتج للبحث..."
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {isLoadingProducts ? (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="caption" sx={{ mr: 1 }}>
                        جاري البحث...
                      </Typography>
                    </Box>
                  ) : (
                    <SearchIcon />
                  )}
                </InputAdornment>
              ),
            }}
          />
          
          {/* Attribute Filters */}
          {attributes.length > 0 && (
            <Box sx={{ mt: 2, mb: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                تصفية حسب خصائص المنتج:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                {attributes.map(attribute => {
                  const availableValues = getAttributeValues(attribute.name);
                  if (availableValues.length === 0) return null;
                  
                  return (
                    <Autocomplete
                      key={attribute._id}
                      multiple
                      options={availableValues}
                      filterSelectedOptions
                      value={selectedAttributes[attribute.name] || []}
                      onChange={(_, newValue) => handleAttributeChange(attribute.name, newValue)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={attribute.name}
                          size="small"
                          sx={{ minWidth: 200 }}
                        />
                      )}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            label={option}
                            size="small"
                            {...getTagProps({ index })}
                          />
                        ))
                      }
                    />
                  );
                })}
              </Box>
            </Box>
          )}
          
          {/* Selected Items Summary */}
          {selectedItems.length > 0 && (
            <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1, border: '1px solid', borderColor: 'primary.main' }}>
              <Typography variant="subtitle2" gutterBottom sx={{ color: 'text.primary', fontWeight: 500 }}>
                العناصر المختارة ({selectedItems.length}):
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {selectedItems.map((item, index) => (
                  <Chip
                    key={`${item.productId}-${item.variantId}-${index}`}
                    label={`${item.productName} - ${item.barcode || ''}`}
                    size="small"
                    onDelete={() => {
                      setSelectedItems(prev => prev.filter(i => i.variantId !== item.variantId));
                    }}
                    color="primary"
                    variant="outlined"
                  />
                ))}
              </Box>
            </Box>
          )}
          
          {/* Products Table */}
          <Typography variant="subtitle1" gutterBottom>
            {searchTerm.trim() ? (
              `النتائج (${filteredProducts.reduce((count, product) => {
                return count + product.variants.filter(variant => {
                  if (productSelectionMode === 'category' && selectedCategories.length > 0) {
                    let variantAllowed = false;
                    
                    if (variant.categories && variant.categories.length > 0) {
                      variantAllowed = variant.categories.some(categoryId => selectedCategories.includes(categoryId));
                    }
                    
                    if (!variantAllowed) {
                      variantAllowed = variant.barcodes.some(barcode => {
                        return barcode.categories && barcode.categories.some(categoryId =>
                          selectedCategories.includes(categoryId)
                        );
                      });
                    }
                    
                    if (!variantAllowed) return false;
                  }
                  
                  if (Object.keys(selectedAttributes).length === 0) return true;
                  
                  return Object.entries(selectedAttributes).every(([attrName, selectedValues]) => {
                    const variantValue = variant.attributes[attrName];
                    return selectedValues.includes(variantValue);
                  });
                }).length;
              }, 0)} متغير)`
            ) : (
              'ابدأ بالبحث لعرض المنتجات'
            )}
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>اسم المنتج</TableCell>
                  <TableCell>متغيرات المنتج</TableCell>
                  <TableCell>الباركود</TableCell>
                  <TableCell>السعر</TableCell>
                  <TableCell>الوحدة</TableCell>
                  <TableCell>إجراءات</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredProducts.map(product => (
                  product.variants.filter(variant => {
                    if (productSelectionMode === 'category' && selectedCategories.length > 0) {
                      let variantAllowed = false;
                      
                      if (variant.categories && variant.categories.length > 0) {
                        variantAllowed = variant.categories.some(categoryId => selectedCategories.includes(categoryId));
                      }
                      
                      if (!variantAllowed) {
                        variantAllowed = variant.barcodes.some(barcode => {
                          return barcode.categories && barcode.categories.some(categoryId =>
                            selectedCategories.includes(categoryId)
                          );
                        });
                      }
                      
                      if (!variantAllowed) {
                        return false;
                      }
                    }
                    
                    if (Object.keys(selectedAttributes).length === 0) return true;
                    
                    return Object.entries(selectedAttributes).every(([attrName, selectedValues]) => {
                      const variantValue = variant.attributes[attrName];
                      return selectedValues.includes(variantValue);
                    });
                  }).map(variant => (
                    <TableRow key={`${product._id}-${variant._id}`}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              cursor: 'pointer',
                              color: 'primary.main',
                              '&:hover': { textDecoration: 'underline' }
                            }}
                            onClick={() => handleShowInventory(product, variant)}
                          >
                            {product.name}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() => handleShowInventory(product, variant)}
                            sx={{ color: 'primary.main' }}
                          >
                            <InventoryIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {Object.entries(variant.attributes).map(([key, value], idx) => (
                          <Chip 
                            key={idx}
                            label={`${key}: ${value}`} 
                            size="small" 
                            sx={{ m: 0.5 }}
                          />
                        ))}
                      </TableCell>
                      <TableCell>
                        {variant.barcodes.map((barcode, idx) => (
                          <Typography key={idx} variant="body2">
                            {barcode.code}
                            {barcode.isDefault && ' (افتراضي)'}
                          </Typography>
                        ))}
                      </TableCell>
                      <TableCell>
                        {variant.barcodes.length > 0 && 
                          `${variant.barcodes[0].retailPrice} ريال`}
                      </TableCell>
                      <TableCell>
                        {variant.barcodes.length > 0 ? variant.barcodes[0].unit || variant.unit || '-' : '-'}
                      </TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          variant={isItemSelected(variant._id || '') ? 'outlined' : 'contained'}
                          color={isItemSelected(variant._id || '') ? 'success' : 'primary'}
                          onClick={() => {
                            if (multiSelect || onSelectVariants) {
                              handleToggleSelection(product, variant);
                            } else if (onSelectVariant) {
                              const selectedVariant: InventoryProductVariantSearchResult = {
                                productId: product._id || '',
                                productName: product.name || '',
                                variantId: variant._id || '',
                                variantAttributes: variant.attributes || {},
                                barcode: variant.barcodes[0]?.code || '',
                                unitId: variant.barcodes[0]?.unit || variant.unit || '',
                                unitName: variant.barcodes[0]?.unit || variant.unit || ''
                              };
                              onSelectVariant(selectedVariant);
                              handleDialogClose();
                            }
                          }}
                        >
                          {isItemSelected(variant._id || '') ? '✓ تم الاختيار' : (multiSelect || onSelectVariants ? 'إضافة' : 'اختيار')}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                ))}
                {!searchTerm.trim() && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Box sx={{ py: 4 }}>
                        <SearchIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                        <Typography variant="body1" color="text.secondary">
                          ابدأ بكتابة اسم المنتج في حقل البحث
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          أو استخدم قارئ الباركود للبحث السريع
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
                {searchTerm.trim() && filteredProducts.length === 0 && !isLoadingProducts && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Box sx={{ py: 4 }}>
                        <Typography variant="body1" color="text.secondary">
                          لا توجد نتائج مطابقة للبحث "{searchTerm}"
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          جرب كلمات بحث أخرى أو استخدم الباركود
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
                {isLoadingProducts && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Box sx={{ py: 4 }}>
                        <Typography variant="body1" color="text.secondary">
                          جاري البحث عن المنتجات...
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={handleDialogClose} color="inherit">
          إلغاء
        </Button>
        {(multiSelect || onSelectVariants) && filteredProducts.length > 0 && (
          <Button
            onClick={handleSelectAll}
            variant="outlined"
            color="secondary"
            sx={{ mr: 1 }}
          >
            تحديد الكل ({getTotalVisibleItems()})
          </Button>
        )}
        {(multiSelect || onSelectVariants) && selectedItems.length > 0 && (
          <Button
            onClick={handleAddSelected}
            variant="contained"
            color="primary"
            startIcon={<CheckCircleIcon />}
          >
            إضافة المحدد ({selectedItems.length})
          </Button>
        )}
      </DialogActions>
    </Dialog>

    {/* Inventory Dialog */}
    <Dialog
      open={inventoryDialogOpen}
      onClose={handleCloseInventoryDialog}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            كميات المخزون - {selectedVariantForInventory?.product.name}
          </Typography>
          <IconButton onClick={handleCloseInventoryDialog} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent dividers>
        {selectedVariantForInventory && (() => {
          console.log('🔍 تشخيص المتغير المحدد:', {
            productName: selectedVariantForInventory.product.name,
            variantId: selectedVariantForInventory.variant._id,
            warehouseInventory: selectedVariantForInventory.variant.warehouseInventory,
            warehouseInventoryLength: selectedVariantForInventory.variant.warehouseInventory?.length || 0,
            allVariantKeys: Object.keys(selectedVariantForInventory.variant)
          });
          
          return (
          <Box>
            {/* Variant Details */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                تفاصيل المتغير:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                {Object.entries(selectedVariantForInventory.variant.attributes).map(([key, value], idx) => (
                  <Chip
                    key={idx}
                    label={`${key}: ${value}`}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                ))}
              </Box>
            </Box>

            {/* Inventory Table */}
            <Typography variant="subtitle1" gutterBottom>
              الكميات في المستودعات:
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>المستودع</TableCell>
                    <TableCell align="right">الكمية الحالية</TableCell>
                    <TableCell align="right">الكمية المحجوزة</TableCell>
                    <TableCell align="right">إجمالي الكمية</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {warehouses.map((warehouse) => {
                    const inventory = selectedVariantForInventory.variant.warehouseInventory?.find(
                      inv => inv.warehouseId === warehouse._id
                    );
                    
                    const currentQuantity = inventory?.availableQuantity || inventory?.quantity || (inventory as any)?.currentQuantity || 0;
                    const reservedQuantity = inventory?.reservedQuantity || 0;
                    const totalQuantity = currentQuantity + reservedQuantity;
                    
                    console.log(`🔍 تشخيص المستودع ${warehouse.name}:`, {
                      warehouseId: warehouse._id,
                      hasInventory: !!inventory,
                      inventory: inventory,
                      availableQuantity: inventory?.availableQuantity,
                      quantity: inventory?.quantity,
                      reservedQuantity: inventory?.reservedQuantity,
                      calculatedCurrentQuantity: currentQuantity,
                      allKeys: inventory ? Object.keys(inventory) : 'لا يوجد مخزون'
                    });
                    
                    return (
                      <TableRow key={warehouse._id}>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {warehouse.name}
                          </Typography>
                          {warehouse.location && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              {warehouse.location}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            variant="body2"
                            color={currentQuantity > 0 ? 'success.main' : 'text.secondary'}
                            fontWeight="bold"
                          >
                            {currentQuantity}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" color="warning.main">
                            {reservedQuantity}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            variant="body2"
                            fontWeight="bold"
                            color={totalQuantity > 0 ? 'primary.main' : 'text.secondary'}
                          >
                            {totalQuantity}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {warehouses.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        <Typography variant="body2" color="text.secondary">
                          لا توجد مستودعات متاحة
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Barcodes */}
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                الباركودات:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {selectedVariantForInventory.variant.barcodes.map((barcode, idx) => (
                  <Chip
                    key={idx}
                    label={`${barcode.code} - ${barcode.retailPrice} ريال${barcode.isDefault ? ' (افتراضي)' : ''}`}
                    size="small"
                    color={barcode.isDefault ? 'primary' : 'default'}
                  />
                ))}
              </Box>
            </Box>
          </Box>
          );
        })()}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={handleCloseInventoryDialog} color="inherit">
          إغلاق
        </Button>
        {selectedVariantForInventory && (multiSelect || onSelectVariants) && (
          <Button
            onClick={() => {
              if (multiSelect || onSelectVariants) {
                handleToggleSelection(selectedVariantForInventory.product, selectedVariantForInventory.variant);
                handleCloseInventoryDialog();
              }
            }}
            variant="contained"
            color="primary"
          >
            إضافة للقائمة
          </Button>
        )}
        {selectedVariantForInventory && !(multiSelect || onSelectVariants) && (
          <Button
            onClick={() => {
              if (onSelectVariant) {
                const selectedVariant: InventoryProductVariantSearchResult = {
                  productId: selectedVariantForInventory.product._id || '',
                  productName: selectedVariantForInventory.product.name || '',
                  variantId: selectedVariantForInventory.variant._id || '',
                  variantAttributes: selectedVariantForInventory.variant.attributes || {},
                  barcode: selectedVariantForInventory.variant.barcodes[0]?.code || '',
                  unitId: selectedVariantForInventory.variant.barcodes[0]?.unit || selectedVariantForInventory.variant.unit || '',
                  unitName: selectedVariantForInventory.variant.barcodes[0]?.unit || selectedVariantForInventory.variant.unit || ''
                };
                onSelectVariant(selectedVariant);
                handleCloseInventoryDialog();
                handleDialogClose();
              }
            }}
            variant="contained"
            color="primary"
          >
            اختيار هذا المتغير
          </Button>
        )}
      </DialogActions>
    </Dialog>
    </>
  );
};

export default InventoryProductSearch;
